import React, { createContext, useContext, useEffect, useMemo, useReducer, useState } from "react";

/**
 * Inquiry Cart state (client-side only, persisted to localStorage).
 * ---------------------------------------------------------------------------
 * Backend readiness: cart lives entirely in the browser. To sync with a
 * backend later, replace the localStorage read/write below with API calls
 * (e.g. persist per authenticated user) — the reducer & consumer API stay same.
 */
const STORAGE_KEY = "athena.inquiryCart.v1";
const InquiryCartContext = createContext(null);

const load = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

function reducer(state, action) {
  switch (action.type) {
    case "ADD": {
      const existing = state.find((i) => i.productId === action.item.productId);
      if (existing) {
        return state.map((i) =>
          i.productId === action.item.productId
            ? { ...i, quantity: i.quantity + (action.item.quantity || 1) }
            : i
        );
      }
      return [...state, { ...action.item, quantity: action.item.quantity || 1, notes: "" }];
    }
    case "REMOVE":
      return state.filter((i) => i.productId !== action.productId);
    case "QTY":
      return state.map((i) =>
        i.productId === action.productId
          ? { ...i, quantity: Math.max(1, action.quantity) }
          : i
      );
    case "NOTES":
      return state.map((i) =>
        i.productId === action.productId ? { ...i, notes: action.notes } : i
      );
    case "CLEAR":
      return [];
    default:
      return state;
  }
}

export function InquiryCartProvider({ children }) {
  const [items, dispatch] = useReducer(reducer, [], load);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {}
  }, [items]);

  const value = useMemo(
    () => ({
      items,
      count: items.reduce((n, i) => n + i.quantity, 0),
      drawerOpen,
      openDrawer: () => setDrawerOpen(true),
      closeDrawer: () => setDrawerOpen(false),
      // item shape: { productId, name, brand, category, image, slug }
      addItem: (item) => dispatch({ type: "ADD", item }),
      removeItem: (productId) => dispatch({ type: "REMOVE", productId }),
      setQuantity: (productId, quantity) => dispatch({ type: "QTY", productId, quantity }),
      setNotes: (productId, notes) => dispatch({ type: "NOTES", productId, notes }),
      clear: () => dispatch({ type: "CLEAR" }),
      has: (productId) => items.some((i) => i.productId === productId),
    }),
    [items, drawerOpen]
  );

  return <InquiryCartContext.Provider value={value}>{children}</InquiryCartContext.Provider>;
}

export const useInquiryCart = () => {
  const ctx = useContext(InquiryCartContext);
  if (!ctx) throw new Error("useInquiryCart must be used within InquiryCartProvider");
  return ctx;
};
